import {
  Modality,
  ReportType,
  LactateTest,
  ZoneBoundary,
  ThresholdEntry,
  Vo2Block,
} from './types';
import { unitsForModality } from './constants';

export const ZONE_BOUNDARY_ORDER: ZoneBoundary['boundary'][] = [
  'Z1_Z2',
  'Z2_Z3',
  'Z3_Z4',
  'Z4_Z5',
  'Z5_Z6_7',
];

export interface ThresholdFormValue {
  intensity: string;
  pace: string;
  hr: string;
  hr_pct_max: string;
}

export interface ZoneBoundaryFormValue {
  boundary: ZoneBoundary['boundary'];
  intensity: string;
  hr: string;
}

export interface Vo2FormValue {
  max_output: string;
  vo2max: string;
  max_hr: string;
  ventilation: string;
  tidal_volume: string;
  resp_frequency: string;
}

export interface LactateFormValues {
  report_type: ReportType;
  modality: Modality;
  vo2_included: boolean;
  meta: {
    date: string;
    age: string;
    height: string;
    weight: string;
    location: string;
    lab: string;
  };
  measured_hr_max: string;
  thresholds: { t1: ThresholdFormValue; t2: ThresholdFormValue };
  zone_boundaries: ZoneBoundaryFormValue[];
  next_steps: string;
  vo2: Vo2FormValue;
}

const emptyThreshold = (): ThresholdFormValue => ({ intensity: '', pace: '', hr: '', hr_pct_max: '' });

const emptyVo2 = (): Vo2FormValue => ({
  max_output: '',
  vo2max: '',
  max_hr: '',
  ventilation: '',
  tidal_volume: '',
  resp_frequency: '',
});

export interface ClientPrefill {
  client_id: string;
  client_name: string;
  primary_location?: string;
  height?: string;
  weight?: string;
}

export function defaultFormValues(client: ClientPrefill, today: string): LactateFormValues {
  return {
    report_type: 'Initial',
    modality: 'treadmill',
    vo2_included: false,
    meta: {
      date: today,
      age: '',
      height: client.height ?? '',
      weight: client.weight ?? '',
      location: client.primary_location ?? '',
      lab: 'UFIT Performance Lab',
    },
    measured_hr_max: '',
    thresholds: { t1: emptyThreshold(), t2: emptyThreshold() },
    zone_boundaries: ZONE_BOUNDARY_ORDER.map((boundary) => ({ boundary, intensity: '', hr: '' })),
    next_steps: '',
    vo2: emptyVo2(),
  };
}

const PACE_RE = /^\d{1,2}:[0-5]\d$/;

const isPositiveNumber = (s: string): boolean => {
  const t = s.trim();
  if (t === '') return false;
  const n = Number(t);
  return Number.isFinite(n) && n > 0;
};

export function validateForm(values: LactateFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  const { showPace } = unitsForModality(values.modality);

  (['t1', 't2'] as const).forEach((key) => {
    const t = values.thresholds[key];
    if (!isPositiveNumber(t.intensity)) errors[`thresholds.${key}.intensity`] = 'Required';
    if (!isPositiveNumber(t.hr)) errors[`thresholds.${key}.hr`] = 'Required';
    if (!isPositiveNumber(t.hr_pct_max)) errors[`thresholds.${key}.hr_pct_max`] = 'Required';
    if (showPace) {
      if (t.pace.trim() === '') errors[`thresholds.${key}.pace`] = 'Required';
      else if (!PACE_RE.test(t.pace.trim())) errors[`thresholds.${key}.pace`] = 'Use mm:ss';
    }
  });

  if (!isPositiveNumber(values.measured_hr_max)) errors['measured_hr_max'] = 'Required';

  values.zone_boundaries.forEach((z, i) => {
    if (!isPositiveNumber(z.intensity)) errors[`zone_boundaries.${i}.intensity`] = 'Required';
    // The top (Z5/Z6-7) boundary has no HR field — Z6/7 is a power zone.
    if (z.boundary !== 'Z5_Z6_7' && !isPositiveNumber(z.hr)) {
      errors[`zone_boundaries.${i}.hr`] = 'Required';
    }
  });

  if (values.vo2_included) {
    (['max_output', 'vo2max', 'max_hr', 'ventilation', 'tidal_volume', 'resp_frequency'] as const).forEach(
      (key) => {
        if (!isPositiveNumber(values.vo2[key])) errors[`vo2.${key}`] = 'Required';
      }
    );
  }

  return errors;
}

const num = (s: string): number => Number(s.trim());

const buildThreshold = (t: ThresholdFormValue, showPace: boolean): ThresholdEntry => {
  const entry: ThresholdEntry = {
    intensity: num(t.intensity),
    hr: num(t.hr),
    hr_pct_max: num(t.hr_pct_max),
  };
  if (showPace && t.pace.trim() !== '') entry.pace = t.pace.trim();
  return entry;
};

export function toLactateTestInput(
  values: LactateFormValues,
  client_id: string,
  client_name: string,
  created_by: string,
  client_email?: string
): Omit<LactateTest, 'created_at' | 'updated_at' | 'pdf_url' | 'report_id' | 'prior_initial_test_id'> {
  const { showPace } = unitsForModality(values.modality);

  const zone_boundaries: ZoneBoundary[] = values.zone_boundaries.map((z) => ({
    boundary: z.boundary,
    intensity: num(z.intensity),
    // The top (Z5/Z6-7) boundary carries no HR — Z6/7 is a power zone.
    ...(z.boundary !== 'Z5_Z6_7' ? { hr: num(z.hr) } : {}),
  }));

  const meta = {
    date: values.meta.date,
    ...(values.meta.age.trim() !== '' ? { age: values.meta.age.trim() } : {}),
    ...(values.meta.height.trim() !== '' ? { height: values.meta.height.trim() } : {}),
    ...(values.meta.weight.trim() !== '' ? { weight: values.meta.weight.trim() } : {}),
    ...(values.meta.location.trim() !== '' ? { location: values.meta.location.trim() } : {}),
    ...(values.meta.lab.trim() !== '' ? { lab: values.meta.lab.trim() } : {}),
  };

  const input: Omit<
    LactateTest,
    'created_at' | 'updated_at' | 'pdf_url' | 'report_id' | 'prior_initial_test_id'
  > = {
    client_id,
    client_name,
    ...(client_email && client_email.trim() !== '' ? { client_email: client_email.trim() } : {}),
    report_type: values.report_type,
    modality: values.modality,
    vo2_included: values.vo2_included,
    meta,
    thresholds: {
      t1: buildThreshold(values.thresholds.t1, showPace),
      t2: buildThreshold(values.thresholds.t2, showPace),
    },
    measured_hr_max: num(values.measured_hr_max),
    zone_boundaries,
    ...(values.next_steps.trim() !== '' ? { next_steps: values.next_steps.trim() } : {}),
    created_by,
  };

  if (values.vo2_included) {
    const vo2: Vo2Block = {
      max_output: num(values.vo2.max_output),
      vo2max: num(values.vo2.vo2max),
      max_hr: num(values.vo2.max_hr),
      ventilation: num(values.vo2.ventilation),
      tidal_volume: num(values.vo2.tidal_volume),
      resp_frequency: num(values.vo2.resp_frequency),
    };
    input.vo2 = vo2;
  }

  return input;
}
